export interface ApiEcosystemModel {
  [x: string]: any;
  authData: AuthData;
  selectedRoute: string;
  showLoginView: string; // login or email code
  showNavbarText: boolean;
  signupData: SignupData;
  loginData: LoginData;
  apis: ExternalApiDetails[];
  applications: CombineApplicationsView;
  selectedApiDetail: ExternalApiDetails;
  selectedApplicationDetail: Application;
  notifications: Notification[];
  user: User;
  resetPasswordEmail: string;
  downloadDocumentURI: string;
  downloadDocumentAttachmentId: string;
  clientSecret?: string;
  contactUsCategories?: string[];
  bannerData?: any;
}

export const defaultState: ApiEcosystemModel = {
  selectedRoute: null,
  showLoginView: 'Login',
  showNavbarText: true,
  signupData: {},
  loginData: {},
  apis: [],
  applications: {},
  selectedApiDetail: undefined,
  selectedApplicationDetail: undefined,
  authData: {
    access_token: '',
    expires_in: undefined,
    id_token: '',
    refresh_token: '',
    token_type: '',
  },
  notifications: [],
  user: {
    _id: '',
    department: '',
    displayName: '',
    firstName: '',
    lastName: '',
    jobTitle: '',
    nuid: '',
    email: '',
    isAuthenticated: false,
    isLoggedOff: false,
    modified: false,
    userRole: undefined,
    userToken: '',
    country: '',
  },
  resetPasswordEmail: '',
  downloadDocumentURI: '',
  downloadDocumentAttachmentId: '',
  contactUsCategories: undefined,
  bannerData: undefined,
};

export interface Notification {
  error: boolean;
  warning: boolean;
  message: string;
  title: string;
  duration?: number;
}

export interface User {
  _id: string;
  department?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  nuid?: string;
  email: string;
  isAuthenticated?: boolean;
  isLoggedOff?: boolean;
  modified?: boolean;
  priorAuthEndpoints?: { region: string; url: string }[];
  userRole?: string | string[];
  pending?: boolean;
  id?: string;
  status?: string;
  country?: string;
  userToken?: string;
  teamGroupDetails?: {
    _id?: string;
    teamGroupName?: string;
    teamGroupDomain?: string;
    isOwner?: boolean;
    teamGroupMembers?: Partial<User>[];
    emailsNotInUsers?: string[];
  };
  teamGroup?: any;
}

export interface UpdateUserData {
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  organizationName?: string;
  country?: string;
  homeAccountId?: string;
  idToken?: string;
  phoneUpdateOnly?: boolean;
}
export interface SsoUser {
  Department?: string;
  DisplayName?: string;
  FirstName?: string;
  LastName?: string;
  'Job Title'?: string;
  sub?: string;
  Email: string;
}

export interface ApiSubscription {
  api?: string;
  emailUpdates?: boolean;
  requestStatus?: string;
}

export interface ApiOwnerDetail {
  displayName?: string;
  email?: string;
}

export interface ConnectionRequest {
  serviceId?: string;
  serviceOwnerEmail?: string;
  externalServiceName?: string;
  serviceDescription?: string;
  receiveEmailNotifications?: boolean;
  status?: string;
  approved?: boolean;
  hasQuestionnaireData?: boolean;
  sandboxEndpoint?: string;
  productionEndpoint?: string;
  visibility?: string;
  limitedServiceAvailability?: boolean;
  note?: string;
  priorAuthEndpoints?: { region: string; url: string }[];
  approvedRegionEndpoints?: { region: string; url: string }[];
}

export interface Application {
  id: string;
  name: string;
  description: string;
  portalClassification?: string;
  version?: string;
  subscriptions?: ApiSubscription[];
  website: string;
  connectionRequestList?: ConnectionRequest[];
  sandboxRedirectUrls?: string[];
  prodRedirectUrls?: string[];
  sandboxJwksUrl?: string;
  prodJwksUrl?: string;
  owner?: string;
  ownerDetails?: ApiOwnerDetail;
  delegates?: ApiOwnerDetail[];
  canGenerateCredentials?: boolean;
  isSubscribed?: boolean;
  hasRevoked?: boolean;
  sandboxClientId?: string;
  sandboxClientSecret?: string;
  prodClientId?: string;
  prodClientSecret?: string;
  sandboxPublicJwks?: string;
  prodPublicJwks?: string;
  sandboxActiveJwksType?: 'jwksUrl' | 'staticJwks' | null;
  prodActiveJwksType?: 'jwksUrl' | 'staticJwks' | null;
  applicationPersona: string;
}

export interface CombineApplicationsView {
  groupApplications?: Application[];
  personalApplications?: Application[];
}

export interface CreateApplication {
  name: string;
  description: string;
  website: string;
}

export interface AuthData {
  access_token: string;
  expires_in: number;
  id_token: string;
  refresh_token: string;
  token_type: string;
}

export interface LoginData {
  email?: string;
  homeAccountId?: string;
  updateUserData?: UpdateUserData;
  idToken?: string;
}
export interface SignupData {
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  organizationName?: string;
  organizationWebsite?: string;
  phoneNumber?: string;
  country?: string;
  homeAccountId?: string;
  idToken?: string;
}

export interface CommonEmailData {
  serviceTitle?: string;
  freeText?: string;
  helpCategory?: string;
}

export interface EmailContent {
  displayName?: string;
  requesterEmail?: string;
}

export interface ContactUsData {
  commonEmailData: CommonEmailData;
  emailContent: EmailContent[];
  firstName?: string;
  lastName?: string;
  piiPhiAttest?: boolean;
}

export interface HttpErrorResponse {
  id: string;
  name: string;
  description: string;
  fhirVersion?: string;
  notes?: any[];
  service: ServiceDetails;
  visibility: string;
  sandboxEndpoint?: string;
  productionEndpoint?: string;
  serviceId?: string;
  isSubscribed?: boolean; // Used to disable apis in Add API list in app-details that have already been requested
}

export interface ExternalApiDetails {
  id: string;
  name: string;
  description: string;
  fhirVersion?: string;
  notes?: any[];
  service: ServiceDetails;
  visibility: string;
  sandboxEndpoint?: string;
  productionEndpoint?: string;
  priorAuthEndpoints?: { region: string; url: string }[];
  serviceId?: string;
  isSubscribed?: boolean; // Used to disable apis in Add API list in app-details that have already been requested
  isRestrictedPartnerService?: boolean;
  hasQuestionnaireData?: boolean;
}

/**
 * Maps API visibility values to their associated persona.
 * public-pending and partner-pending are intentionally excluded — they are
 * not yet published and should not unlock a persona.
 * partner-restricted is included as developer because access is enforced
 * server-side (whitelist); the persona is still developer.
 */
export const VISIBILITY_TO_PERSONA_MAP: Record<string, string> = {
  public: 'developer',
  partner: 'developer',
  'partner-restricted': 'developer',
  'other-interoperability': 'developer',
  developer: 'developer',
  payer: 'payer',
  provider: 'provider',
  'prior-auth': 'prior-auth',
};

/**
 * Derives the distinct set of available persona values from an API list
 * by mapping each API's visibility through VISIBILITY_TO_PERSONA_MAP.
 * Returns an empty array when the list is empty or no API maps to a persona.
 */
export const getAvailablePersonasFromApis = (apis: ExternalApiDetails[]): string[] => {
  if (!apis?.length) {
    return [];
  }
  const personas = new Set<string>();
  apis.forEach(api => {
    const persona = VISIBILITY_TO_PERSONA_MAP[api?.visibility?.toLowerCase()];
    if (persona) {
      personas.add(persona);
    }
  });
  return Array.from(personas);
};

export interface ServiceDetails {
  name?: string;
  description?: string;
  version?: string;
  keywords?: string[];
  operations?: string[];
  interfaceType?: string;
  notes?: any[];
  externalAttachments?: ExternalAttachments[];
}

export interface ExternalAttachments {
  id: string;
  name: string;
  tag: string;
  fileDisplayName: string;
}

export interface ClientSecretData {
  clientSecretType: string;
  clientSecret: string;
}

export interface CreateApplicationData {
  name: string;
  description: string;
  website: string;
}

export interface UpdateAppWebsiteData {
  website: string;
  appId: string;
}

export interface UpdateAppOwnerData {
  emailId: string;
  appId: string;
}

export interface UpdateDelegateIdsData {
  delegates: string[];
  appId: string;
}

export interface UriData {
  persona?: string;
  redirectUrls?: string[];
  appId: string;
  isProd: boolean;
  jwksUrl?: string;
  sandboxPublicJwks?: string;
  prodPublicJwks?: string;
}
export interface GenerateClientIdData {
  appId: string;
  isPrimary: boolean;
  persona?: string;
  jwksUrl?: string;
}
export interface GeneratePayerClientIdData {
  appId: string;
  isPrimary: boolean;
  persona?: string;
  jwksUrl?: string;
}
export interface BannerData {
  bannerType: string;
  bannerText?: string;
}

export interface SandboxAvailabilityData {
  sandboxUnderConstruction: boolean;
  sandboxUnderConstructionText?: string;
  sandboxKeyType?: 'JWKS' | 'JWK';
  productionKeyType?: 'JWKS' | 'JWK';
  sandboxJwkJson?: string;
  productionJwkJson?: string;
}

export enum AttachmentTypeTag {
  OPENAPICONFIGURATION = 'openapi-specification',
  IMPLEMENTATIONGUIDE = 'implementation-guide',
  ADDITIONALINFORMATION = 'additional-information',
  OTHER = 'other',
}

export enum BannerTypes {
  UNDERCONSTRUCTION = 'under-construction',
  NOTES = 'notes',
  NONE = 'none',
}

export interface AddUserToTeamGroupType {
  usersToAddEmails: string[];
  teamGroupName: string;
}

export interface PromoteRemoveUserInTeamGroup {
  email?: string;
  teamGroupName?: string;
  teamGroupId?: string;
  removedByOwner?: boolean;
}

export enum ExternalUserRole {
  INTERNAL = 'internal',
  ADMIN = 'admin',
  EXTERNAL = 'external',
  EXTERNAL_PARTNER = 'external_partner',
  EXTERNAL_KP_TEST_ACCOUNT = 'external_kp_test_account',
  ANONYMOUS = 'anonymous',
  PAYER = 'payer',
  PROVIDER = 'provider',
  DEVELOPER = 'developer',
  PRIOR_AUTH = 'prior-auth',
}

// FAQ Types
export enum SectionElementType {
  PLAIN_TEXT = 'plain_text',
  NUMBERED_LIST = 'numbered_list',
  BULLET_LIST = 'bullet_list',
}

export enum ContentBlockType {
  PLAIN_TEXT = 'plain_text',
  NUMBERED_LIST = 'numbered_list',
  BULLET_LIST = 'bullet_list',
}

export interface Link {
  text: string;
  url: string;
}

export interface ContentBlock {
  type: ContentBlockType;
  content: string;
  listItems?: string[];
  link?: Link;
  inlineLinks?: Link[];
}

export interface SectionElement {
  type: SectionElementType;
  content: string;
  listItems?: string[];
  link?: Link;
  inlineLinks?: Link[];
  contentBlocks?: ContentBlock[];
}

export interface FaqSection {
  title: string;
  header?: string;
  description?: string;
  elements?: SectionElement[];
}

export interface Faq {
  _id?: string;
  title: string;
  header: string;
  sections?: FaqSection[];
  isActive?: boolean;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
}
